import React, { useCallback } from "react";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import { CRMAccountCard } from "../../../../components/CRMAccountCard";
import { ReactComponent as ContactIcon } from "./assets/contact.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";

export const WhatsappClientsContactDetailsTab: React.FC = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { whatsappAgent } = useWhatsappAccount();
  const { integrationsApi } = useRestApiProvider();
  const [updateWhatsappAgentAccount, { isLoading }] =
    integrationsApi.useUpdateWhatsappAgentAccountMutation();

  const handleUpdateWhatsappAgentAccount = useCallback(async () => {
    if (!whatsappAgent) return;

    const response = await updateWhatsappAgentAccount({
      entityId,
      hideNumber: !whatsappAgent.settings.hide_num,
    }).unwrap();

    if (response) {
      toast(
        <span>
          {response.settings.hide_num
            ? t({
                id: "client-support-page.setting-hide-client-contact-hidden",
                defaultMessage: `Clients contact details visibility set to “Hidden”`,
              })
            : t({
                id: "client-support-page.setting-hide-client-contact-visible",
                defaultMessage: `Clients contact details visibility set to “Visible”`,
              })}
        </span>
      );
    }
  }, [entityId, whatsappAgent, updateWhatsappAgentAccount, toast, t]);

  if (!whatsappAgent) return null;
  return (
    <>
      <CRMAccountCard
        title={t({
          id: "client-support-page.client-contact-details",
          defaultMessage: "Clients contact details",
        })}
        description={t({
          id: "client-support-page.client-contact-details-visible",
          defaultMessage: "Clients contact details is fully visible",
        })}
        status={whatsappAgent?.settings.hide_num ? "hidden" : "visible"}
        switchProps={{
          isChecked: !whatsappAgent?.settings.hide_num,
          onChange: handleUpdateWhatsappAgentAccount,
          isDisabled: isLoading,
        }}
        icon={ContactIcon}
      />
      <CRMInfoSection
        title={t({
          id: "client-support-page.tools-and-tips",
          defaultMessage: "Tools & Tips",
        })}
        subtitle={t({
          id: "client-support-page.everything-you-need-to-know",
          defaultMessage: "Everything you need to know:",
        })}
        items={[
          {
            icon: <UserIcon />,
            title: t({
              id: "client-support-page.contacts-visibility",
              defaultMessage: "Contacts visibility",
            }),
            tips: [
              t({
                id: "client-support-page.clients-details-visible",
                defaultMessage: `If Clients contact details is "Visible", Client's phone number will be fully visible`,
              }),
              t({
                id: "client-support-page.clients-details-hidden",
                defaultMessage: `If Clients contact details is "Hidden", only the first 3 and the last 4 digits of the Client's phone number will be visible`,
              }),
            ],
          },
        ]}
      />
    </>
  );
};
